import React, { useState, useEffect, useRef } from "react";
import { createWord, updateWord, searchVocabularies } from "./adminSimilarWordsService";
import ImagePickerModal from "../../media_images/ImagePickerModal";
import "../../styles/content.css";

const ROOT = process.env.REACT_APP_BASE_URL_ROOT;
const toPublicImgSrc = (url) => {
  if (!url) return "";
  const pub = url.replace(/\/api(?:\/admin)?\/media-images\//, "/api/public/media-images/");
  const src = pub.startsWith("http") ? pub : `${ROOT}${pub}`;
  console.log("[WordForm] img raw:", url, "→ resolved:", src);
  return src;
};

const WordForm = ({ word, wordGroupId, onSaved, onCancel }) => {
  const isEdit = !!word;
  console.log("[WordForm] render — word prop:", word, "| isEdit:", isEdit, "| wordGroupId:", wordGroupId);

  const [form, setForm] = useState({
    word: "", pronunciation: "", phoneticSpelling: "", meaning: "", examples: "",
    isActive: true, displayOrder: 1, imageUrl: "", vocabularyId: null,
  });
  const [loading, setLoading] = useState(false);
  const [showImagePicker, setShowImagePicker] = useState(false);

  // Vocabulary search state (create mode only)
  const [vocabSearch, setVocabSearch] = useState("");
  const [vocabResults, setVocabResults] = useState([]);
  const [vocabSearching, setVocabSearching] = useState(false);
  const [linkedVocab, setLinkedVocab] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const debounceRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (word) {
      setForm({
        word: word.word || "", pronunciation: word.pronunciation || "",
        phoneticSpelling: word.phoneticSpelling || "",
        meaning: word.meaning || "", examples: word.examples || "",
        isActive: word.isActive !== false, displayOrder: word.displayOrder || 1,
        imageUrl: word.imageUrl || "", vocabularyId: word.vocabularyId || null,
      });
    }
  }, [word]);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleVocabSearchChange = (e) => {
    const val = e.target.value;
    setVocabSearch(val);
    setShowDropdown(true);
    clearTimeout(debounceRef.current);
    if (val.trim().length < 2) { setVocabResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      console.log("[WordForm] searching vocab:", val.trim());
      setVocabSearching(true);
      const res = await searchVocabularies(val.trim());
      console.log("[WordForm] vocab search result:", res.status, res.data?.content?.length, "items");
      if (res.status === 200) setVocabResults(res.data?.content || []);
      else setVocabResults([]);
      setVocabSearching(false);
    }, 300);
  };

  const handleVocabSelect = (vocab) => {
    console.log("[WordForm] vocab selected:", vocab.id, vocab.word);
    setLinkedVocab(vocab);
    setForm((prev) => {
      const next = {
        ...prev,
        vocabularyId: vocab.id,
        word: vocab.word || prev.word,
        pronunciation: vocab.pronunciation || prev.pronunciation,
        phoneticSpelling: vocab.phoneticSpelling || prev.phoneticSpelling,
        meaning: vocab.definition || prev.meaning,
      };
      console.log("[WordForm] auto-filled form:", next);
      return next;
    });
    setVocabSearch("");
    setVocabResults([]);
    setShowDropdown(false);
  };

  const handleVocabClear = () => {
    console.log("[WordForm] vocab cleared, keeping field values");
    setLinkedVocab(null);
    setForm((prev) => ({ ...prev, vocabularyId: null }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === "checkbox" ? checked : value });
  };

  const handleSubmit = async () => {
    if (!form.word || !form.meaning) { alert("Word and Meaning are required"); return; }
    const payload = {
      ...form,
      wordGroupId,
      displayOrder: Number(form.displayOrder) || 1,
      imageUrl: form.imageUrl || null,
      vocabularyId: form.vocabularyId || null,
    };
    console.log("[WordForm] submit — isEdit:", isEdit, "| wordGroupId:", wordGroupId, "| payload:", payload);
    setLoading(true);
    const res = isEdit ? await updateWord(word.id, payload) : await createWord(payload);
    console.log("[WordForm] submit result:", res.status, res.data || res.message);
    if (res.status === 200 || res.status === 201) { onSaved(); }
    else { alert(res.message || "Operation failed"); }
    setLoading(false);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ width: 520, maxHeight: "90vh", overflowY: "auto" }}>
        <div className="form-card" style={{ margin: 0, boxShadow: "none" }}>
          <h2>{isEdit ? "Edit" : "Add"} Word</h2>

          {/* ── Vocabulary Linker (create mode) ── */}
          {console.log("[WordForm] rendering vocab linker section — isEdit:", isEdit)}
          {!isEdit && (
            <div style={{ background: "#f0f7ff", border: "1px solid #bfdbfe", borderRadius: 10, padding: "14px 16px", marginBottom: 18 }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: "#1e40af", marginBottom: 8 }}>
                🔗 Link to existing Vocabulary (optional)
              </div>
              <div style={{ fontSize: 12, color: "#555", marginBottom: 10 }}>
                Search and select a vocabulary to auto-fill the fields below. You can still edit them after.
              </div>

              {linkedVocab ? (
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#dbeafe", color: "#1e40af", padding: "5px 14px", borderRadius: 20, fontSize: 13, fontWeight: 600 }}>
                    ✓ {linkedVocab.word}
                    <button type="button" onClick={handleVocabClear}
                      style={{ background: "none", border: "none", cursor: "pointer", color: "#1e40af", fontSize: 16, lineHeight: 1, padding: "0 0 0 4px" }}>✕</button>
                  </span>
                  <span style={{ fontSize: 12, color: "#666" }}>Fields auto-filled — edit freely</span>
                </div>
              ) : (
                <div style={{ position: "relative" }} ref={dropdownRef}>
                  <input
                    type="text"
                    placeholder="Type at least 2 characters to search vocabulary..."
                    value={vocabSearch}
                    onChange={handleVocabSearchChange}
                    onFocus={() => vocabResults.length > 0 && setShowDropdown(true)}
                    autoComplete="off"
                    style={{ width: "100%", boxSizing: "border-box" }}
                  />
                  {showDropdown && (vocabSearching || vocabResults.length > 0 || vocabSearch.length >= 2) && (
                    <div style={{ position: "absolute", zIndex: 20, width: "100%", background: "#fff", border: "1px solid #ddd", borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.12)", maxHeight: 200, overflowY: "auto", marginTop: 4 }}>
                      {vocabSearching ? (
                        <div style={{ padding: 12, fontSize: 13, color: "#888", textAlign: "center" }}>Searching...</div>
                      ) : vocabResults.length === 0 ? (
                        <div style={{ padding: 12, fontSize: 13, color: "#888", textAlign: "center" }}>No vocabularies found for "{vocabSearch}"</div>
                      ) : (
                        vocabResults.map((v) => (
                          <button key={v.id} type="button" onClick={() => handleVocabSelect(v)}
                            style={{ width: "100%", textAlign: "left", padding: "9px 14px", background: "none", border: "none", borderBottom: "1px solid #f0f0f0", cursor: "pointer", fontSize: 13 }}
                            onMouseEnter={(e) => e.currentTarget.style.background = "#eff6ff"}
                            onMouseLeave={(e) => e.currentTarget.style.background = "none"}>
                            <div style={{ fontWeight: 600, color: "#1a1a1a" }}>{v.word}</div>
                            {v.definition && (
                              <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>
                                {v.definition.length > 70 ? v.definition.slice(0, 70) + "…" : v.definition}
                              </div>
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Edit mode: show linked badge if vocabularyId exists ── */}
          {isEdit && word?.vocabularyId && (
            <div style={{ background: "#f0f7ff", border: "1px solid #bfdbfe", borderRadius: 10, padding: "10px 16px", marginBottom: 18 }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "#1e40af", fontSize: 13, fontWeight: 500 }}>
                🔗 Linked to vocabulary #{word.vocabularyId}
              </span>
            </div>
          )}

          <div className="form-group"><label>Word *</label><input name="word" placeholder="e.g. Break" value={form.word} onChange={handleChange} /></div>
          <div className="form-group"><label>Pronunciation (IPA)</label><input name="pronunciation" placeholder="/breɪk/" value={form.pronunciation} onChange={handleChange} /></div>
          <div className="form-group"><label>Phonetic Spelling</label><input name="phoneticSpelling" placeholder="e.g. brayk" value={form.phoneticSpelling} onChange={handleChange} /></div>
          <div className="form-group"><label>Meaning *</label><textarea name="meaning" placeholder="To separate into pieces; to pause or rest" value={form.meaning} onChange={handleChange} rows={2} /></div>
          <div className="form-group"><label>Examples</label><textarea name="examples" placeholder="Let's take a break from studying. The glass will break if you drop it." value={form.examples} onChange={handleChange} rows={3} /></div>
          <div className="form-group"><label>Display Order</label><input name="displayOrder" type="number" min="1" value={form.displayOrder} onChange={handleChange} /></div>

          <div className="form-group">
            <label>Image (optional)</label>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              {form.imageUrl ? (
                <img src={toPublicImgSrc(form.imageUrl)} alt="selected"
                  style={{ width: 60, height: 60, objectFit: "contain", background: "#f5f5f5", borderRadius: 8, border: "1px solid #ddd" }} />
              ) : (
                <div style={{ width: 60, height: 60, background: "#f0f0f0", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "#aaa" }}>No image</div>
              )}
              <button type="button" className="btn secondary" onClick={() => setShowImagePicker(true)}>Pick Image</button>
              {form.imageUrl && (
                <button type="button" className="btn secondary" onClick={() => setForm({ ...form, imageUrl: "" })}>Remove</button>
              )}
            </div>
          </div>

          <div className="form-group">
            <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input name="isActive" type="checkbox" checked={form.isActive} onChange={handleChange} /> Active
            </label>
          </div>

          <div className="form-actions">
            <button onClick={handleSubmit} disabled={loading}>{loading ? "Saving..." : isEdit ? "Update" : "Add"}</button>
            <button onClick={onCancel} disabled={loading}>Cancel</button>
          </div>
        </div>
      </div>

      <ImagePickerModal
        isOpen={showImagePicker}
        onClose={() => setShowImagePicker(false)}
        onSelect={(url) => { setForm({ ...form, imageUrl: url }); setShowImagePicker(false); }}
        defaultImageUse="VOCABULARY"
      />
    </div>
  );
};

export default WordForm;
